import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    ButtonBase,
    Collapse,
    Divider,
    Typography,
    useTheme,
} from "@mui/material";
import { AltArrowDown } from "@solar-icons/react/ssr";
import { useDispatch, useSelector } from "react-redux";

import LogoButton from "@/nav/LogoButton";
import {
    collapsedIconGap,
    collapsedIconLeft,
    collapsedNavItemWidth,
    navIconSize,
    sideNavCollapsedHoverOffset,
    sideNavCollapsedWidth,
    sideNavContentId,
    sideNavHoverOffset,
    sideNavTransition,
    sideNavTransitionDuration,
    sideNavTransitionEasing,
    sideNavWidth,
    supportCollapseDuration,
} from "@/nav/navConstants";
import { NavProvider, useNavContext } from "@/nav/NavContext";
import { getRouteBase } from "@/nav/navHooks";
import { NavItemIcon } from "@/nav/NavIcons";
import { selectNavToggleHovered, setNavToggleHovered } from "@/nav/navSlice";
import { getSideNavColors } from "@/nav/navTheme";
import OrganizationSwitcher from "@/nav/OrganizationSwitcher";

const SIDENAV_TRANSITION_DURATION = sideNavTransitionDuration;
const SIDENAV_LABEL_EXIT_DURATION = SIDENAV_TRANSITION_DURATION;
const SIDENAV_EASING = sideNavTransitionEasing;
const SIDENAV_TRANSITION = sideNavTransition;
const SUPPORT_COLLAPSE_DURATION = supportCollapseDuration;
const SIDE_NAV_CONTENT_ID = sideNavContentId;
const COLLAPSED_NAV_ITEM_WIDTH = collapsedNavItemWidth;
const NAV_ICON_SIZE = navIconSize;
const COLLAPSED_ICON_GAP = collapsedIconGap;
const COLLAPSED_ICON_LEFT = collapsedIconLeft;

const getNavItemTestId = (title) =>
    `nav-item-${title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "")}`;

const SideNav = memo(() => {
    return (
        <NavProvider>
            <SideNavContent />
        </NavProvider>
    );
});

function SideNavContent() {
    const theme = useTheme();
    const dispatch = useDispatch();
    const isToggleHovered = useSelector(selectNavToggleHovered);
    const {
        actions: { activateItem, commitNavOpen, selectOrganization },
        state: {
            activeOrganization,
            currentRoute,
            isHidden,
            isSmall,
            menuItemsMain,
            menuItemsUser,
            menus,
            open,
            organizations,
        },
    } = useNavContext();
    const [renderExpandedContent, setRenderExpandedContent] = useState(open);
    const [openMenuTitle, setOpenMenuTitle] = useState(null);
    const [collapsedTooltip, setCollapsedTooltip] = useState(null);
    const [collapsedSubMenu, setCollapsedSubMenu] = useState(null);
    const pendingNavCloseRef = useRef(null);
    const subMenuCloseTimeoutRef = useRef(null);
    const accountMenuItems = menuItemsUser.filter(
        (item) => item.title != "Logout",
    );
    const navMenus = menus.filter((menu) => menu.items.length > 0);
    const signOutItem = menuItemsUser.find((item) => item.title == "Logout");
    const colors = useMemo(() => getSideNavColors(theme), [theme]);

    useEffect(() => {
        let labelExitTimeout;

        if (open) {
            labelExitTimeout = setTimeout(() => {
                setRenderExpandedContent(true);
            }, 0);
        } else {
            labelExitTimeout = setTimeout(() => {
                setRenderExpandedContent(false);
            }, SIDENAV_LABEL_EXIT_DURATION);
        }

        return () => clearTimeout(labelExitTimeout);
    }, [open]);

    useEffect(() => {
        const closeTooltipTimeout = setTimeout(() => {
            setCollapsedTooltip(null);
        }, 0);

        return () => clearTimeout(closeTooltipTimeout);
    }, [open]);

    useEffect(
        () => () => {
            clearTimeout(pendingNavCloseRef.current);
            clearTimeout(subMenuCloseTimeoutRef.current);
        },
        [],
    );

    const handleNavItemClick = useCallback(
        (item) => () => {
            activateItem(item);
        },
        [activateItem],
    );

    const handleToggleNav = useCallback(() => {
        const nextOpen = !open;
        clearTimeout(pendingNavCloseRef.current);

        if (!nextOpen && openMenuTitle) {
            setOpenMenuTitle(null);
            pendingNavCloseRef.current = setTimeout(() => {
                commitNavOpen(false);
            }, SUPPORT_COLLAPSE_DURATION);
            return;
        }

        commitNavOpen(nextOpen);
    }, [commitNavOpen, open, openMenuTitle]);

    const handleToggleHoverChange = useCallback(
        (hovered) => {
            dispatch(setNavToggleHovered(hovered));
        },
        [dispatch],
    );

    const handleCollapsedTooltipOpen = useCallback(
        (label, anchorElement) => {
            if (open || !anchorElement) {
                return;
            }

            const rect = anchorElement.getBoundingClientRect();
            const iconRight = rect.left + COLLAPSED_ICON_LEFT + NAV_ICON_SIZE;
            clearTimeout(subMenuCloseTimeoutRef.current);
            setCollapsedSubMenu(null);
            setCollapsedTooltip({
                label,
                left: iconRight + COLLAPSED_ICON_GAP,
                top: rect.top + 1.5,
            });
        },
        [open],
    );

    const handleCollapsedTooltipClose = useCallback(() => {
        setCollapsedTooltip(null);
    }, []);

    const handleCollapsedSubMenuOpen = useCallback(
        (title, items, anchorElement) => {
            if (open || !anchorElement) {
                return;
            }

            const rect = anchorElement.getBoundingClientRect();
            const iconRight = rect.left + COLLAPSED_ICON_LEFT + NAV_ICON_SIZE;
            clearTimeout(subMenuCloseTimeoutRef.current);
            setCollapsedTooltip(null);
            setCollapsedSubMenu({
                items,
                title,
                left: iconRight + COLLAPSED_ICON_GAP,
                top: rect.top,
            });
        },
        [open],
    );

    const handleCollapsedSubMenuClose = useCallback(() => {
        clearTimeout(subMenuCloseTimeoutRef.current);
        setCollapsedSubMenu(null);
    }, []);

    const handleCollapsedSubMenuCloseDelayed = useCallback(() => {
        clearTimeout(subMenuCloseTimeoutRef.current);
        subMenuCloseTimeoutRef.current = setTimeout(() => {
            setCollapsedSubMenu(null);
        }, 90);
    }, []);

    const handleCollapsedSubMenuKeepOpen = useCallback(() => {
        clearTimeout(subMenuCloseTimeoutRef.current);
    }, []);

    if (isSmall || isHidden) {
        return null;
    }

    const baseNavWidth = open ? sideNavWidth : sideNavCollapsedWidth;
    const hoverDelta = isToggleHovered
        ? open
            ? -sideNavHoverOffset
            : sideNavCollapsedHoverOffset
        : 0;
    const edgeWidth = baseNavWidth + hoverDelta;

    return (
        <>
            <Box
                component="nav"
                aria-label="Primary navigation"
                data-testid="side-nav"
                sx={{
                    position: "fixed",
                    top: 0,
                    bottom: 0,
                    left: 0,
                    zIndex: 1300,
                    width: edgeWidth,
                    height: "100vh",
                    boxSizing: "border-box",
                    overflow: "visible",
                    backgroundColor: colors.background,
                    borderRight: `1px solid ${colors.border}`,
                    color: colors.navText,
                    transition: `width ${SIDENAV_TRANSITION}`,
                }}
            >
                <Box
                    id={SIDE_NAV_CONTENT_ID}
                    sx={{
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        left: 0,
                        display: "flex",
                        flexDirection: "column",
                        width: baseNavWidth,
                        height: "100%",
                        minHeight: 0,
                        boxSizing: "border-box",
                        transition: `width ${SIDENAV_TRANSITION}`,
                    }}
                >
                    <Box sx={{ flex: "0 0 auto" }}>
                        <OrganizationSwitcher.Desktop
                            activeOrganization={activeOrganization}
                            collapsed={!open}
                            expanded={open}
                            indicatorHovered={isToggleHovered}
                            organizations={organizations}
                            renderExpandedContent={renderExpandedContent}
                            onSelectOrganization={selectOrganization}
                        />

                        <Divider
                            sx={{
                                margin: open
                                    ? "24px 20px 36px"
                                    : "30px 20px 36px",
                                borderColor: colors.divider,
                            }}
                        />
                    </Box>

                    <Box
                        sx={{
                            flex: "1 1 auto",
                            minHeight: 0,
                            overflowY: "auto",
                            overflowX: "visible",
                            scrollbarWidth: "none",
                            "&::-webkit-scrollbar": {
                                display: "none",
                            },
                        }}
                    >
                        <Box
                            sx={{
                                margin: open ? "0 20px 0 16px" : 0,
                                padding: open ? 0 : "0 0",
                                transition: `margin ${SIDENAV_TRANSITION}, padding ${SIDENAV_TRANSITION}`,
                            }}
                        >
                            <SideNavList>
                                {menuItemsMain.map((item) => (
                                    <MemoSideNavItem
                                        key={item.title}
                                        item={item}
                                        active={
                                            getRouteBase(item.url) ==
                                            currentRoute
                                        }
                                        collapsed={!open}
                                        expanded={open}
                                        renderExpandedContent={
                                            renderExpandedContent
                                        }
                                        onTooltipClose={
                                            handleCollapsedTooltipClose
                                        }
                                        onTooltipOpen={
                                            handleCollapsedTooltipOpen
                                        }
                                        onClick={handleNavItemClick(item)}
                                    />
                                ))}
                            </SideNavList>
                        </Box>

                        {(accountMenuItems.length > 0 ||
                            navMenus.length > 0) && (
                            <Box
                                sx={{
                                    margin: open
                                        ? "43px 20px 0 16px"
                                        : "43px 0 0",
                                    padding: open ? "0 4px" : 0,
                                    transition: `margin ${SIDENAV_TRANSITION}, padding ${SIDENAV_TRANSITION}`,
                                }}
                            >
                                {renderExpandedContent && (
                                    <Typography
                                        sx={{
                                            height: open ? 16 : 0,
                                            display: "flex",
                                            alignItems: "center",
                                            margin: open
                                                ? "0 0 5px 4px"
                                                : "0 0 0 4px",
                                            color: colors.subtitle,
                                            fontSize: 12,
                                            fontWeight: 400,
                                            lineHeight: "100%",
                                            letterSpacing: 0,
                                            opacity: open ? 1 : 0,
                                            overflow: "visible",
                                            transform: open
                                                ? "translateY(0)"
                                                : "translateY(-6px)",
                                            filter: open
                                                ? "blur(0)"
                                                : "blur(4px)",
                                            transition: `height ${SIDENAV_TRANSITION}, margin ${SIDENAV_TRANSITION}, opacity ${SIDENAV_TRANSITION}, transform ${SIDENAV_TRANSITION}, filter ${SIDENAV_TRANSITION}`,
                                        }}
                                    >
                                        Manage
                                    </Typography>
                                )}

                                <SideNavList>
                                    {accountMenuItems.map((item) => (
                                        <MemoSideNavItem
                                            key={item.title}
                                            item={item}
                                            active={
                                                getRouteBase(item.url) ==
                                                currentRoute
                                            }
                                            collapsed={!open}
                                            expanded={open}
                                            renderExpandedContent={
                                                renderExpandedContent
                                            }
                                            onTooltipClose={
                                                handleCollapsedTooltipClose
                                            }
                                            onTooltipOpen={
                                                handleCollapsedTooltipOpen
                                            }
                                            onClick={handleNavItemClick(item)}
                                        />
                                    ))}
                                    {navMenus.map((menu) => (
                                        <MemoSideNavGroup
                                            key={menu.title}
                                            collapsed={!open}
                                            currentRoute={currentRoute}
                                            expanded={open}
                                            menu={menu}
                                            open={openMenuTitle == menu.title}
                                            onOpenChange={(nextOpen) => {
                                                setOpenMenuTitle(
                                                    nextOpen
                                                        ? menu.title
                                                        : null,
                                                );
                                            }}
                                            onItemClick={handleNavItemClick}
                                            onSubMenuClose={
                                                handleCollapsedSubMenuCloseDelayed
                                            }
                                            onSubMenuOpen={
                                                handleCollapsedSubMenuOpen
                                            }
                                            onTooltipClose={
                                                handleCollapsedTooltipClose
                                            }
                                            onTooltipOpen={
                                                handleCollapsedTooltipOpen
                                            }
                                            renderExpandedContent={
                                                renderExpandedContent
                                            }
                                        />
                                    ))}
                                </SideNavList>
                            </Box>
                        )}
                    </Box>

                    <Box
                        sx={{
                            flex: "0 0 auto",
                            padding: open ? "0 20px 26px 16px" : "0 0 26px",
                            transition: `padding ${SIDENAV_TRANSITION}`,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-start",
                                marginBottom: "24px",
                            }}
                        >
                            {signOutItem && (
                                <MemoSideNavItem
                                    item={signOutItem}
                                    active={false}
                                    collapsed={!open}
                                    expanded={open}
                                    renderExpandedContent={
                                        renderExpandedContent
                                    }
                                    onTooltipClose={handleCollapsedTooltipClose}
                                    onTooltipOpen={handleCollapsedTooltipOpen}
                                    onClick={handleNavItemClick(signOutItem)}
                                />
                            )}
                        </Box>

                        <Divider
                            sx={{
                                margin: open ? "0 0 24px 4px" : "0 8.5px 24px",
                                borderColor: colors.divider,
                            }}
                        />

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                height: 7,
                            }}
                        >
                            <LogoButton
                                sx={{
                                    display: "block",
                                    width: "auto",
                                    height: 7,
                                }}
                            />
                        </Box>
                    </Box>

                    <MemoSideNavToggle
                        collapsed={!open}
                        controlsId={SIDE_NAV_CONTENT_ID}
                        edgeWidth={edgeWidth}
                        onClick={handleToggleNav}
                        onHoverChange={handleToggleHoverChange}
                    />
                </Box>
            </Box>
            <SideNavFloatingTooltip tooltip={collapsedTooltip} />
            <SideNavCollapsedSubMenu
                menu={collapsedSubMenu}
                onClose={handleCollapsedSubMenuClose}
                onCloseDelayed={handleCollapsedSubMenuCloseDelayed}
                onItemClick={handleNavItemClick}
                onKeepOpen={handleCollapsedSubMenuKeepOpen}
            />
        </>
    );
}

SideNav.displayName = "SideNav";

function SideNavList({ children }) {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                alignItems: "flex-start",
            }}
        >
            {children}
        </Box>
    );
}

function SideNavItem({
    active,
    collapsed,
    expanded,
    item,
    onClick,
    onTooltipClose,
    onTooltipOpen,
    nested = false,
    renderExpandedContent,
}) {
    const theme = useTheme();
    const colors = getSideNavColors(theme);
    const label = item.title;
    const handleTooltipOpen = (event) => {
        if (collapsed) {
            onTooltipOpen?.(label, event.currentTarget);
        }
    };

    return (
        <ButtonBase
            disableRipple
            onClick={onClick}
            onBlur={onTooltipClose}
            onFocus={handleTooltipOpen}
            onMouseEnter={handleTooltipOpen}
            onMouseLeave={onTooltipClose}
            aria-current={active ? "page" : undefined}
            aria-label={label}
            data-testid={getNavItemTestId(item.title)}
            sx={{
                width: collapsed ? COLLAPSED_NAV_ITEM_WIDTH : "100%",
                minWidth: collapsed ? COLLAPSED_NAV_ITEM_WIDTH : 0,
                height: collapsed ? 24 : 23,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: "8px",
                marginLeft: collapsed ? "14px" : 0,
                padding: collapsed
                    ? `0 ${COLLAPSED_ICON_LEFT}px`
                    : nested
                      ? "5px 5px 5px 22px"
                      : "5px 5px 5px 5px",
                boxSizing: "border-box",
                borderRadius: "5px",
                color: active ? colors.active : colors.navText,
                textAlign: "left",
                backgroundColor: "transparent",
                overflow: "visible",
                position: "relative",
                transform: "translateY(0)",
                transition: `background-color 160ms ease, color 160ms ease, transform 160ms ease, margin-left ${SIDENAV_TRANSITION}, padding ${SIDENAV_TRANSITION}, width ${SIDENAV_TRANSITION}`,
                "&:hover": {
                    color: active ? colors.active : colors.hoverText,
                    backgroundColor: colors.hoverBackground,
                    transform: "translateY(-1px)",
                },
                "&:active, &.Mui-focusVisible": {
                    color: active ? colors.active : colors.hoverText,
                    backgroundColor: colors.hoverBackground,
                },
            }}
        >
            <NavItemIcon icon={item.icon} size={13} />
            {renderExpandedContent && (
                <SideNavLabel
                    expanded={expanded}
                    label={label}
                    maxWidth={nested ? 100 : 120}
                />
            )}
        </ButtonBase>
    );
}

function SideNavLabel({ expanded, label, maxWidth }) {
    return (
        <Typography
            sx={{
                color: "inherit",
                display: "inline-flex",
                alignItems: "center",
                height: 10,
                width: expanded ? maxWidth : 0,
                maxWidth: expanded ? maxWidth : 0,
                opacity: expanded ? 1 : 0,
                overflow: "visible",
                whiteSpace: "nowrap",
                fontSize: 14,
                fontWeight: 400,
                lineHeight: "100%",
                letterSpacing: 0,
                filter: expanded ? "blur(0)" : "blur(4px)",
                transform: expanded ? "translateY(0)" : "translateY(-6px)",
                transition: `opacity ${SIDENAV_TRANSITION}, filter ${SIDENAV_TRANSITION}, transform ${SIDENAV_TRANSITION}, max-width ${SIDENAV_TRANSITION}, width ${SIDENAV_TRANSITION}`,
            }}
        >
            {label}
        </Typography>
    );
}

function SideNavFloatingTooltip({ tooltip }) {
    const theme = useTheme();
    const colors = getSideNavColors(theme);

    if (!tooltip) {
        return null;
    }

    return (
        <Box
            sx={{
                position: "fixed",
                left: tooltip.left,
                top: tooltip.top,
                zIndex: 2000,
                minWidth: 0,
                height: 20,
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "5px 8px",
                boxSizing: "border-box",
                borderRadius: "5px",
                backgroundColor: colors.floatingSurface,
                color: colors.tooltipText,
                pointerEvents: "none",
                transform: "translateZ(0)",
                whiteSpace: "nowrap",
            }}
        >
            <Typography
                component="span"
                sx={{
                    color: "inherit",
                    display: "inline-flex",
                    alignItems: "center",
                    height: 10,
                    overflow: "visible",
                    fontSize: 14,
                    fontWeight: 400,
                    lineHeight: "100%",
                    letterSpacing: 0,
                    whiteSpace: "nowrap",
                }}
            >
                {tooltip.label}
            </Typography>
        </Box>
    );
}

function SideNavGroup({
    collapsed,
    currentRoute,
    expanded,
    menu,
    open,
    onOpenChange,
    onItemClick,
    onSubMenuClose,
    onSubMenuOpen,
    onTooltipClose,
    onTooltipOpen,
    renderExpandedContent,
}) {
    const theme = useTheme();
    const colors = getSideNavColors(theme);
    const title = menu.title;
    const groupContentId = `${getNavItemTestId(menu.title)}-items`;
    const hasActiveChild = menu.items.some(
        (item) => getRouteBase(item.url) == currentRoute,
    );
    const handleTooltipOpen = (event) => {
        if (!collapsed) {
            return;
        }

        if (menu.items.length > 0) {
            onSubMenuOpen?.(title, menu.items, event.currentTarget);
            return;
        }

        onTooltipOpen?.(title, event.currentTarget);
    };
    const handleHoverLeave = () => {
        if (collapsed && menu.items.length > 0) {
            onSubMenuClose?.();
            return;
        }

        onTooltipClose?.();
    };

    return (
        <Box
            sx={{
                width: collapsed ? COLLAPSED_NAV_ITEM_WIDTH : "100%",
                marginLeft: collapsed ? "14px" : 0,
                transition: `margin-left ${SIDENAV_TRANSITION}, width ${SIDENAV_TRANSITION}`,
            }}
        >
            <ButtonBase
                disableRipple
                onClick={() => onOpenChange(!open)}
                onBlur={handleHoverLeave}
                onFocus={handleTooltipOpen}
                onMouseEnter={handleTooltipOpen}
                onMouseLeave={handleHoverLeave}
                aria-controls={groupContentId}
                aria-expanded={open}
                aria-haspopup={collapsed ? "menu" : undefined}
                aria-label={title}
                data-testid={getNavItemTestId(menu.title)}
                sx={{
                    width: collapsed ? COLLAPSED_NAV_ITEM_WIDTH : "100%",
                    height: collapsed ? 24 : 23,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: collapsed ? `0 ${COLLAPSED_ICON_LEFT}px` : "5px",
                    boxSizing: "border-box",
                    borderRadius: "5px",
                    color: hasActiveChild ? colors.active : colors.navText,
                    backgroundColor: "transparent",
                    overflow: "visible",
                    position: "relative",
                    transform: "translateY(0)",
                    transition: `background-color 160ms ease, color 160ms ease, transform 160ms ease, padding ${SIDENAV_TRANSITION}, width ${SIDENAV_TRANSITION}`,
                    "&:hover": {
                        color: hasActiveChild
                            ? colors.active
                            : colors.hoverText,
                        backgroundColor: colors.hoverBackground,
                        transform: "translateY(-1px)",
                    },
                    "&:active, &.Mui-focusVisible": {
                        backgroundColor: colors.hoverBackground,
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        minWidth: 0,
                    }}
                >
                    <NavItemIcon icon={menu.icon} size={13} />
                    {renderExpandedContent && (
                        <SideNavLabel
                            expanded={expanded}
                            label={title}
                            maxWidth={100}
                        />
                    )}
                </Box>
                {renderExpandedContent && <RotatingChevron open={open} />}
            </ButtonBase>

            {renderExpandedContent && (
                <Collapse
                    id={groupContentId}
                    in={open}
                    timeout={SUPPORT_COLLAPSE_DURATION}
                    unmountOnExit
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "10px",
                            margin: "5px 0 0 9px",
                            padding: "0 0 0 10px",
                            borderLeft: `1px solid ${colors.divider}`,
                        }}
                    >
                        {menu.items.map((item) => (
                            <SupportSubMenuOption
                                key={item.title}
                                active={getRouteBase(item.url) == currentRoute}
                                item={item}
                                onClick={onItemClick(item)}
                            />
                        ))}
                    </Box>
                </Collapse>
            )}
        </Box>
    );
}

function SupportSubMenuOption({ active, item, onClick }) {
    const theme = useTheme();
    const colors = getSideNavColors(theme);
    const label = item.title;

    return (
        <ButtonBase
            disableRipple
            onClick={onClick}
            aria-current={active ? "page" : undefined}
            aria-label={label}
            data-testid={getNavItemTestId(item.title)}
            sx={{
                width: "100%",
                minHeight: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                padding: "5px 8px",
                boxSizing: "border-box",
                borderRadius: "5px",
                color: active ? colors.active : colors.navText,
                textAlign: "left",
                transition: `background-color ${SIDENAV_TRANSITION}, color ${SIDENAV_TRANSITION}, transform 160ms ease`,
                "&:hover": {
                    color: active ? colors.active : colors.hoverText,
                    backgroundColor: colors.hoverBackground,
                    transform: "translateY(-1px)",
                },
                "&:active, &.Mui-focusVisible": {
                    color: active ? colors.active : colors.hoverText,
                    backgroundColor: colors.hoverBackground,
                },
            }}
        >
            <Typography
                sx={{
                    color: "inherit",
                    display: "inline-flex",
                    alignItems: "center",
                    height: 10,
                    overflow: "visible",
                    whiteSpace: "nowrap",
                    fontSize: 14,
                    fontWeight: 400,
                    lineHeight: "100%",
                    letterSpacing: 0,
                }}
            >
                {label}
            </Typography>
        </ButtonBase>
    );
}

function SideNavCollapsedSubMenu({
    menu,
    onClose,
    onCloseDelayed,
    onItemClick,
    onKeepOpen,
}) {
    const theme = useTheme();
    const colors = getSideNavColors(theme);

    if (!menu) {
        return null;
    }

    return (
        <Box
            onMouseEnter={onKeepOpen}
            onMouseLeave={onCloseDelayed}
            role="menu"
            aria-label={menu.title}
            sx={{
                position: "fixed",
                left: menu.left,
                top: menu.top,
                zIndex: 2000,
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                padding: "14px 8px",
                boxSizing: "border-box",
                borderRadius: "5px",
                width: 147,
                backgroundColor: colors.floatingSurface,
                color: colors.tooltipText,
                whiteSpace: "nowrap",
            }}
        >
            <Typography
                sx={{
                    height: 9,
                    marginLeft: "8px",
                    display: "inline-flex",
                    alignItems: "center",
                    overflow: "visible",
                    color: colors.subtitle,
                    fontSize: 12,
                    fontWeight: 400,
                    lineHeight: "100%",
                    letterSpacing: 0,
                    whiteSpace: "nowrap",
                }}
            >
                {menu.title}
            </Typography>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                }}
            >
                {menu.items.map((item) => (
                    <CollapsedSubMenuOption
                        key={item.title}
                        item={item}
                        onClick={() => {
                            onClose();
                            onItemClick(item)();
                        }}
                    />
                ))}
            </Box>
        </Box>
    );
}

function CollapsedSubMenuOption({ item, onClick }) {
    const theme = useTheme();
    const colors = getSideNavColors(theme);

    return (
        <ButtonBase
            disableRipple
            onClick={onClick}
            role="menuitem"
            aria-label={item.title}
            sx={{
                width: "100%",
                minHeight: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                padding: "5px 8px",
                boxSizing: "border-box",
                borderRadius: "5px",
                color: colors.tooltipText,
                textAlign: "left",
                transition: `background-color ${SIDENAV_TRANSITION}, color ${SIDENAV_TRANSITION}, transform 160ms ease`,
                "&:hover": {
                    backgroundColor: colors.hoverBackground,
                    color: colors.hoverText,
                    transform: "translateY(-1px)",
                },
                "&:active, &.Mui-focusVisible": {
                    backgroundColor: colors.hoverBackground,
                    color: colors.hoverText,
                },
            }}
        >
            <Typography
                sx={{
                    color: "inherit",
                    display: "inline-flex",
                    alignItems: "center",
                    height: 10,
                    overflow: "visible",
                    whiteSpace: "nowrap",
                    fontSize: 14,
                    fontWeight: 400,
                    lineHeight: "100%",
                    letterSpacing: 0,
                }}
            >
                {item.title}
            </Typography>
        </ButtonBase>
    );
}

function RotatingChevron({ open, size = 13 }) {
    return (
        <NavItemIcon
            icon={AltArrowDown}
            size={size}
            sx={{
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                transformOrigin: "center",
                transition: `transform ${SIDENAV_TRANSITION}`,
            }}
        />
    );
}

function SideNavToggle({
    collapsed,
    controlsId,
    edgeWidth,
    onClick,
    onHoverChange,
}) {
    const [hovered, setHovered] = useState(false);
    const theme = useTheme();
    const colors = getSideNavColors(theme);
    const tooltipTitle = collapsed ? "Expand" : "Collapse";
    const indicatorLineOffset = 10;
    const indicatorHalfWidth = 10;
    const indicatorGap = 3;
    const tooltipLeft =
        edgeWidth + indicatorLineOffset + indicatorHalfWidth + indicatorGap;

    const handleMouseEnter = () => {
        setHovered(true);
        onHoverChange(true);
    };

    const handleMouseLeave = () => {
        setHovered(false);
        onHoverChange(false);
    };

    return (
        <>
            <ButtonBase
                disableRipple
                data-sidenav-toggle="true"
                data-testid="side-nav-toggle"
                onClick={onClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                aria-controls={controlsId}
                aria-expanded={!collapsed}
                aria-label={`${tooltipTitle} navigation`}
                sx={{
                    position: "absolute",
                    left: edgeWidth,
                    bottom: 46,
                    width: 68,
                    height: 22,
                    display: "grid",
                    alignItems: "center",
                    justifyItems: "start",
                    borderRadius: "4px",
                    backgroundColor: "transparent",
                    color: colors.indicator,
                    transition: `left ${SIDENAV_TRANSITION}`,
                    "&:hover": {
                        backgroundColor: "transparent",
                    },
                    "&:active, &.Mui-focusVisible": {
                        backgroundColor: "transparent",
                    },
                }}
            >
                <AnimatedCollapseIndicator
                    collapsed={collapsed}
                    hovered={hovered}
                />
            </ButtonBase>
            <Box
                aria-hidden={!hovered}
                sx={{
                    position: "absolute",
                    left: tooltipLeft,
                    bottom: 46.5,
                    height: 21,
                    display: hovered ? "flex" : "none",
                    alignItems: "center",
                    padding: "0 8px",
                    borderRadius: "5px",
                    backgroundColor: colors.indicatorTooltipBackground,
                    color: colors.tooltipText,
                    fontSize: 12,
                    fontWeight: 400,
                    lineHeight: "100%",
                    opacity: 1,
                    pointerEvents: "none",
                    whiteSpace: "nowrap",
                    transition: `left ${SIDENAV_TRANSITION}`,
                }}
            >
                <Typography
                    component="span"
                    sx={{
                        color: "inherit",
                        display: "inline-flex",
                        alignItems: "center",
                        height: 9,
                        overflow: "visible",
                        fontSize: 12,
                        fontWeight: 400,
                        lineHeight: "100%",
                        letterSpacing: 0,
                        whiteSpace: "nowrap",
                    }}
                >
                    {tooltipTitle}
                </Typography>
            </Box>
        </>
    );
}

function AnimatedCollapseIndicator({ collapsed, hovered }) {
    const arrowPath = collapsed
        ? "M9.5 4.5L14.031 10.3903C14.3075 10.7497 14.3075 11.2503 14.031 11.6097L9.5 17.5"
        : "M10.5 4.5L5.96901 10.3903C5.69251 10.7497 5.69251 11.2503 5.96901 11.6097L10.5 17.5";

    return (
        <Box
            component="svg"
            viewBox="0 0 20 22"
            aria-hidden="true"
            sx={{
                position: "absolute",
                left: 0,
                width: 20,
                height: 22,
                display: "block",
                color: "inherit",
                overflow: "visible",
            }}
        >
            <Box
                component="line"
                x1="10.5"
                y1="4.5"
                x2="10.5"
                y2="17.5"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="1"
                sx={{
                    opacity: hovered ? 0 : 1,
                    strokeDasharray: 13,
                    strokeDashoffset: hovered ? 13 : 0,
                    transform: hovered ? "scaleY(0.7)" : "scaleY(1)",
                    transformBox: "fill-box",
                    transformOrigin: "center",
                    transition: `opacity 180ms ${SIDENAV_EASING}, stroke-dashoffset ${SIDENAV_TRANSITION}, transform ${SIDENAV_TRANSITION}`,
                }}
            />
            <Box
                component="path"
                d={arrowPath}
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1"
                sx={{
                    opacity: hovered ? 1 : 0,
                    strokeDasharray: 15,
                    strokeDashoffset: hovered ? 0 : 15,
                    transform: hovered ? "scale(1)" : "scale(0.92)",
                    transformBox: "fill-box",
                    transformOrigin: "center",
                    transition: `opacity 300ms ${SIDENAV_EASING}, stroke-dashoffset ${SIDENAV_TRANSITION}, transform ${SIDENAV_TRANSITION}`,
                }}
            />
        </Box>
    );
}
const MemoSideNavGroup = memo(SideNavGroup);
const MemoSideNavItem = memo(SideNavItem);
const MemoSideNavToggle = memo(SideNavToggle);

export default SideNav;
